<!-- ConfiguracionesGenerales.vue -->
<template>
  <div class="bg-white rounded-lg p-0 contenedor-modal-evento w-full">
    <!-- Encabezado tipo folder tabs -->
    <div class="tabs">
      <button class="tab" :class="{ active: activeTab === 'campos' }" @click="activeTab = 'campos'" type="button">
        Campos y reglas
      </button>
      <button class="tab" :class="{ active: activeTab === 'config' }" @click="activeTab = 'config'" type="button">
        Configuración del evento
      </button>
      <button class="tab" :class="{ active: activeTab === 'acceso' }" @click="activeTab = 'acceso'" type="button">
        Acceso público y correo
      </button>
      <button class="tab" :class="{ active: activeTab === 'documentos' }" @click="activeTab = 'documentos'" type="button">
        Documentos
      </button>
      <div class="tabs-spacer" />
    </div>

    <form @submit.prevent="onSubmit" class="tab-card">
      <!-- Contenido de pestañas (v-show para mantener estado) -->
      <section v-show="activeTab === 'campos'" class="tab-pane">
        <CamposPermitidos
          :evento="evento"
          :campos="campos"
          :campos-visible="camposVisible"
          @toggle-duplicados="onToggleDuplicados"
          @update-campo="onUpdateCampo"
          @add-campo="onAddCampo"
          @edit-campo="onEditCampo"
          @remove-campo="onRemoveCampo"
          @reorder-campos="onReorderCampos"
          @set-tipo-asistente="onSetTipoAsistente"
        />
      </section>

      <section v-show="activeTab === 'config'" class="tab-pane">
        <ConfiguracionesEvento :evento="evento" @patch-evento="onPatchEvento" @sanitize-url="sanitizeURL" />
      </section>

      <section v-show="activeTab === 'acceso'" class="tab-pane">
        <AccesoPublicoYCorreo :evento="evento" @patch-evento="onPatchEvento" :saved-at="lastSavedAt" />
      </section>

      <section v-show="activeTab === 'documentos'" class="tab-pane">
        <DocumentosEvento :evento="evento" :base-url="baseUrl" />
      </section>

      <!-- Footer de acciones -->
      <div v-if="activeTab !== 'documentos'" class="card-footer">
        <button type="submit" class="btn-primary">Guardar cambios</button>
      </div>
    </form>
  </div>
</template>
<script setup lang="js">
import { ref, watch, defineProps, defineEmits, nextTick } from 'vue'
import CamposPermitidos from './CamposPermitidos/CamposPermitidos.vue'
import ConfiguracionesEvento from './ConfiguracionesEvento/ConfiguracionesEvento.vue'
import AccesoPublicoYCorreo from './AccesoPublicoYCorreo/AccesoPublicoYCorreo.vue'
import DocumentosEvento from './DocumentosEvento/DocumentosEvento.vue'

const props = defineProps({
  evento: { type: Object, required: true },
  campos: { type: Array, required: true },
  camposVisible: { type: Boolean, required: true },
  baseUrl: { type: String, default: '/api' }
})

const lastSavedAt = ref(0)

const evDefaults = {
  auto_staff: '',
  auto_asistente: '',
  auto_expositor: '',
  auto_staff_en: '',
  auto_asistente_en: '',
  auto_expositor_en: '',
  permitir_ingreso_publico: false,
  gestion_empresas_pagos: false,
  sin_limites_carga_usuarios: false,
  mail_custom: '',
  pass_mail_custom: '',
  server_mail_custom: '',
  type_smpt: '',
  type_security: '',
  port: '',
  api: {}
}

const emit = defineEmits(['saved', 'save-error'])

const activeTab = ref('campos')

const evento = ref(structuredClone(props.evento || {}))
const campos = ref((props.campos || []).map(c => ({ ...c })))
const camposVisible = ref(!!props.camposVisible)

watch(
  () => props.evento,
  v => {
    const incoming = structuredClone(v || {})
    evento.value = {
      ...evDefaults,
      ...incoming,
      api: { ...(evDefaults.api || {}), ...(incoming.api || {}) }
    }
  },
  { immediate: true, deep: true }
)
watch(() => props.campos, v => { campos.value = (v || []).map(c => ({ ...c })) })
watch(() => props.camposVisible, v => { camposVisible.value = !!v })

function onToggleDuplicados() {
  if (evento.value?.familia?.has_family) return
  evento.value.permitir_duplicados = !evento.value.permitir_duplicados
  if (!evento.value.permitir_duplicados) camposVisible.value = true
}

function sameId(a, b) {
  if (a == null || b == null) return false
  return String(a) === String(b)
}

function onUpdateCampo({ id, name, patch }) {
  const idx = campos.value.findIndex(c => (id != null && sameId(c.id_campo, id)) || (name && c.name_campo === name))
  if (idx !== -1) Object.assign(campos.value[idx], patch)
}

function onAddCampo(newCampo) {
  const key = newCampo?.name_campo
  if (!key) return

  const exists = campos.value.some(c => c.name_campo === key)
  if (exists) return window.alert('Ya existe un campo con ese label.')

  campos.value.push({ ...newCampo, is_custom: true, orden: campos.value.length + 1 })
}

function onEditCampo({ prev_name, patch }) {
  if (!prev_name) return
  const idx = campos.value.findIndex(c => c.name_campo === prev_name)
  if (idx === -1) return

  const nextName = patch?.name_campo
  if (nextName && nextName !== prev_name) {
    const exists = campos.value.some((c, i) => i !== idx && c.name_campo === nextName)
    if (exists) return window.alert('Ya existe otro campo con ese label.')
  }

  campos.value[idx] = { ...campos.value[idx], ...patch, is_custom: true }
}

function onRemoveCampo({ id, name }) {
  const idx = campos.value.findIndex(c => (id != null && sameId(c.id_campo, id)) || (name && c.name_campo === name))
  if (idx === -1) return
  campos.value.splice(idx, 1)
  resequenceCampos()
}

function resequenceCampos() {
  campos.value = (campos.value || []).map((campo, idx) => ({ ...campo, orden: idx + 1 }))
}

function onReorderCampos({ fromIndex, toIndex }) {
  const from = Number(fromIndex)
  const to = Number(toIndex)
  if (!Number.isInteger(from) || !Number.isInteger(to)) return
  if (from < 0 || to < 0 || from === to) return
  if (from >= campos.value.length || to >= campos.value.length) return

  const next = campos.value.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  campos.value = next
  resequenceCampos()
}

function onSetTipoAsistente(tipo) {
  evento.value.tipo_asistente = tipo
}

function onPatchEvento(patch) {
  if (patch && typeof patch === 'object') {
    if ('api' in patch) {
      if (!evento.value.api) evento.value.api = {}
      Object.assign(evento.value.api, patch.api || {})
      const { ...rest } = patch
      Object.assign(evento.value, rest)
    } else {
      Object.assign(evento.value, patch)
    }
  }
}

function sanitizeURL() {
  const ev = evento.value
  if (!ev.api) ev.api = {}
  let url = ev.api.url_evento || ''
  url = url.replace(/\s+/g, '').replace(/^(?:https?:\/\/)?(?:www\.)?/, '').replace(/\/.*$/, '')
  ev.api.url_evento = url
}

async function onSubmit() {
  await nextTick()
  sanitizeURL()

  resequenceCampos()

  const camposInfo = (campos.value || []).map((c, idx) => ({
    id_campo: c.id_campo ?? null,
    label: c.name_campo,
    nombre_del_campo: c.nombre_del_campo ?? null,
    nombre_del_campo_en: c.nombre_del_campo_en ?? '',
    tipo_campo: c.tipo_campo ?? 'texto',

    // ✅ NUEVO
    tipo_ui: c.tipo_ui ?? 'input',
    opciones: Array.isArray(c.opciones) ? c.opciones : [],

    activo: !!c.activo,
    requerido: !!c.requerido,
    mandatorio: !!c.mandatorio,
    custom: !!c.is_custom,
    heredado_de_evento_id: c.heredado ? (evento.value.evento_padre_id || null) : null,
    orden: Number(c.orden) > 0 ? Number(c.orden) : (idx + 1)
  }))

  const ev = structuredClone(evento.value)
  const payload = {
    ...ev,
    permitir_duplicados: ev.permitir_duplicados ? 1 : 0,
    permitir_ingreso_publico: ev.permitir_ingreso_publico ? '1' : '0',
    gestion_empresas_pagos: (ev.gestion_empresas_pagos === true || ev.gestion_empresas_pagos === 1 || ev.gestion_empresas_pagos === '1') ? '1' : '0',
    sin_limites_carga_usuarios: (ev.sin_limites_carga_usuarios === true || ev.sin_limites_carga_usuarios === 1 || ev.sin_limites_carga_usuarios === '1') ? '1' : '0',
    campos: camposInfo
  }

  try {
    const res = await fetch(`${props.baseUrl}/eventos/update/${ev.token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const data = await res.json()
    if (!res.ok || data?.status === 'error') {
      throw new Error(data?.message || 'No se pudieron guardar los cambios')
    }
    lastSavedAt.value = Date.now()
    emit('saved', { payload, response: data })
  } catch (e) {
    console.error('Error al actualizar el evento', e)
    emit('save-error', e?.message || 'No se pudieron guardar los cambios')
  }
}
</script>

<style scoped lang="scss">
.contenedor-modal-evento {
  max-width: 1000px;
}

/* ===== Tabs estilo folder ===== */
.tabs {
  position: relative;
  display: flex;
  gap: 0.25rem;
  padding: 0 0.75rem 0 0.75rem;
  /* línea base de la carpeta */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: #e5e7eb; /* gray-200 */
    z-index: 0;
  }
}
.tab {
  position: relative;
  z-index: 1;
  padding: 0.6rem 1rem 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  background: #f9fafb; /* gray-50 */
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
  top: 1px; /* que pisen un poquito la línea base */
}
.tab:hover {
  background: #fff;
}
.tab.active {
  background: #fff;
  color: #111827; /* gray-900 */
  transform: translateY(1px);
  box-shadow: 0 1px 0 0 #fff inset;
}
.tabs-spacer {
  flex: 1 1 auto;
}

/* Tarjeta debajo de tabs */
.tab-card {
  position: relative;
  z-index: 1;
  margin-top: -1px;
  border: 1px solid #e5e7eb; /* mismo borde que tabs */
  background: #fff;
  border-radius: 0 0.75rem 0.75rem 0.75rem;
  padding: 1rem;
}

/* Footer de acciones dentro de la tarjeta */
.card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid #f3f4f6; /* gray-100 */
  margin-top: 1rem;
  padding-top: 1rem;
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  font-weight: 600;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  transition: background 0.15s ease;
}
.btn-primary:hover {
  background: #1d4ed8;
}

/* Ajustes previos de layout: ahora el contenido es 100% */
form {
  display: block;
}
</style>
